'use client';

import Image from 'next/image';
import { ReactNode, useState } from 'react';

import {
    Box,
    BoxContent,
    Button,
    Columns,
    ColumnsItem,
    Icon,
    IconName,
    IconSize,
    Link,
    Modal,
    ModalFooter,
    Sticker,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react-ts';

import { getAppConfig } from '@services/config';

import { PlanType } from '@app-types/product';

import { Cms } from '@components/cms';
import { SSIBlock } from '@components/ssi-block';

import { useHome } from '@landing/hooks';

interface ProductDetailProps {
    product: PlanType | null;
}

export function ProductDetail({ product }: Readonly<ProductDetailProps>) {
    const { selectedOption } = useHome();
    const appConfig = getAppConfig();
    const [isModalOpen, setIsModalOpen] = useState(false);
    if (!product) {
        return null;
    }
    const is5gProduct = !!selectedOption?.gencode || product.maxConnections === '5g';

    const isSummerEdition = product.gencode === appConfig.offers.summerEditionGencode;

    return (
        <Box shadowless>
            <BoxContent background={TrilogyColor.GREY10} className='is-flex is-vertical gap-medium'>
                <Feature iconName={is5gProduct ? IconName.UI_5G : IconName.UI_4G}>{product.networkText}</Feature>

                <Feature iconName={IconName.AIR_TRAVEL}>
                    {isSummerEdition ? (
                        <>
                            <Sticker small className='byou-sticker'>
                                Summer edition
                            </Sticker>
                            <p>
                                <strong>{product.roamingEnvelope}</strong>
                                <span>
                                    &nbsp;{product.fromAbroad}&nbsp;
                                    <SummerEditionFlags />
                                </span>
                            </p>
                        </>
                    ) : (
                        <>
                            <strong>{product.roamingEnvelope} </strong>
                            <Cms as='span' content={product.fromAbroad} />
                        </>
                    )}
                </Feature>

                <Feature iconName={IconName.PHONE_INFINITY}>
                    <Cms as='span' content={product.smsMms} />
                </Feature>
                <Feature iconName={IconName.HAND_MOBILE}>
                    <Cms as='span' content="Assistance 24h/24 via l'application" />
                </Feature>

                <Link role='button' onClick={() => setIsModalOpen(true)}>
                    *&nbsp;Voir les destinations {isSummerEdition ? 'incluses sur Summer edition' : 'en Europe'}
                </Link>
                <Modal
                    closeIcon
                    active={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={isSummerEdition ? 'Destinations incluses sur Summer edition' : 'Destinations en Europe'}
                    contentClassNames='has-text-left'
                >
                    {isSummerEdition ? (
                        <SummerEditionModalContent />
                    ) : (
                        <SSIBlock
                            url='/partials/mobiles/banniere_forfaits-mobiles_destinations_europe'
                            tagId='destinations_europe'
                        />
                    )}

                    <ModalFooter className='is-flex is-justified-end'>
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            variant={TrilogyColor.PRIMARY}
                            className='has-byou-background'
                        >
                            Fermer
                        </Button>
                    </ModalFooter>
                </Modal>
            </BoxContent>
        </Box>
    );
}

const Feature = ({ iconName, children }: { iconName: IconName; children?: ReactNode }) => {
    return (
        <Columns mobile marginless verticalCentered multiline>
            <ColumnsItem narrow className='is-paddingless is-flex'>
                <Icon size={IconSize.SMALL} name={iconName} className='me-3' />
            </ColumnsItem>
            <ColumnsItem className='is-paddingless'>
                <Text level={TextLevels.TWO} marginless typo={TypographyAlign.TEXT_LEFT}>
                    {children}
                </Text>
            </ColumnsItem>
        </Columns>
    );
};

const SummerEditionModalContent = () => {
    const [shouldShowEuropeDestinations, setShouldShowEuropeDestinations] = useState(false);

    return (
        <>
            <SummerEditionList />
            {shouldShowEuropeDestinations ? (
                <Box className='summer-edition-list' background={TrilogyColor.GREY10}>
                    <BoxContent>
                        <Icon name={IconName.TIMES} onClick={() => setShouldShowEuropeDestinations(false)} />
                        <SSIBlock
                            url='/partials/mobiles/banniere_forfaits-mobiles_destinations_europe'
                            tagId='destinations_europe'
                        />
                    </BoxContent>
                </Box>
            ) : (
                <Link onClick={() => setShouldShowEuropeDestinations(true)}>*Voir les destinations en Europe</Link>
            )}
        </>
    );
};

const SummerEditionList = () => {
    return (
        <dl className='dl is-alternate'>
            <dt className='is-3 text'>40 Go utilisables depuis plus de 110 destinations à l’étranger :</dt>
            <dd>
                <b>A&nbsp;:</b>
                &nbsp;Afrique du Sud, Algérie, Arabie Saoudite, Argentine, Australie,&nbsp;
                <b>B&nbsp;:</b>
                &nbsp;Bénin, Bolivie (État plurinational de), Brésil,&nbsp;
                <b>C&nbsp;:</b>
                &nbsp;Cameroun, Canada, Cap-Vert, Chine, Colombie, Comores, République de Corée (Corée du Sud), Côte
                d&apos;Ivoire,&nbsp;
                <b>E&nbsp;:</b>
                &nbsp;Égypte, États-Unis,&nbsp;
                <b>G&nbsp;:</b>
                &nbsp;Guatemala,&nbsp;
                <b>H&nbsp;:</b>
                &nbsp;Honduras, Hong Kong,&nbsp;
                <b>I&nbsp;:</b>
                &nbsp;Île Maurice, Inde, Israël,&nbsp;
                <b>J&nbsp;:</b>
                &nbsp;Japon, Jordanie,&nbsp;
                <b>K&nbsp;:</b>
                &nbsp;Kazakhstan,&nbsp;
                <b>M&nbsp;:</b>
                &nbsp;Macao, Madagascar, Malaisie, Mali, Maroc, Mexique, Monténégro,&nbsp;
                <b>N&nbsp;:</b>
                &nbsp;Nouvelle-Zélande,&nbsp;
                <b>P&nbsp;:</b>
                &nbsp;Pakistan, Paraguay, Pérou, Porto Rico,&nbsp;
                <b>Q&nbsp;:</b>
                &nbsp;Qatar,&nbsp;
                <b>R&nbsp;:</b>
                &nbsp;République Dominicaine, Russie (La fédération de),&nbsp;
                <b>S&nbsp;:</b>
                &nbsp;Sénégal, Serbie, Singapour,&nbsp;
                <b>T&nbsp;:</b>
                &nbsp;Thaïlande, Tunisie, Turquie
            </dd>
            <br />
        </dl>
    );
};

const SummerEditionFlags = () => {
    const path = 'https://bouyguestelecom.fr/assets/media/original/image/CMS/ecommerce/byou';
    return (
        <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'nowrap', verticalAlign: 'bottom' }}>
            <Image
                src={`${path}/switzerland.svg`}
                alt='Suisse'
                width={20}
                height={16}
                style={{ verticalAlign: 'bottom' }}
            />
            <Image src={`${path}/maroc.svg`} alt='Maroc' width={20} height={16} style={{ verticalAlign: 'bottom' }} />
            <Image
                src={`${path}/usa.svg`}
                alt='Etats-Unis'
                width={20}
                height={16}
                style={{ verticalAlign: 'bottom' }}
            />
            <span style={{ marginLeft: 4 }}>...</span>
        </div>
    );
};
